import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { capture } from "@/lib/analytics";

const MILESTONES = [25, 50, 75, 100];

/**
 * Global scroll-depth milestones (25/50/75/100% of the page's total
 * scrollable height) — one hook, mounted once in SiteLayout, covers
 * every page. Milestones reset on every route change so a visitor
 * who already hit 100% on one page starts fresh on the next. Fires
 * at most once per milestone per page view.
 */
export const useScrollDepthTracking = () => {
  const { pathname } = useLocation();
  const reachedRef = useRef(new Set());

  useEffect(() => {
    reachedRef.current = new Set();
    let ticking = false;

    const checkDepth = () => {
      ticking = false;
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const depthPct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      for (const milestone of MILESTONES) {
        if (depthPct >= milestone && !reachedRef.current.has(milestone)) {
          reachedRef.current.add(milestone);
          capture("scroll_depth", { depth_pct: milestone, page_path: pathname });
        }
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(checkDepth);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [pathname]);
};
